/**
 * Quạt thành phẩm trên báo giá (anh Tiến 07/10/2026, mặc định A): NV nhập lưu lượng, cột áp, công suất động cơ…
 * vào quy cách dòng; giá nhập tay. Chưa có công thức chọn quạt, chưa BOM (để sau).
 * Định dạng quy cách theo cách Asia đang ghi: "Q=30.000 m3/h, H=250 Pa, P=4 kW".
 */
export interface FanSpec {
  /** Lưu lượng (m³/h) */
  airflowM3h: number;
  /** Cột áp (Pa) */
  pressurePa: number;
  /** Công suất động cơ (kW) */
  motorKw: number;
  /** VD "380V/3 pha" */
  voltage?: string | null;
  /** Tốc độ (vòng/phút) */
  rpm?: number | null;
  /** VD "250°C/2h" (quạt hút khói) */
  heatRating?: string | null;
}

export const FAN_VOLTAGES = ['220V/1 pha', '380V/3 pha'] as const;
const SPEC_MAX = 255;

/** Mã quạt thành phẩm: tên bắt đầu bằng "Quạt" (không gồm cánh quạt, hộp bọc quạt, cos quạt…). */
export const isFanName = (name: string | null | undefined) => /^\s*quạt(?!\p{L})/iu.test((name ?? '').normalize('NFC'));

const vn = (v: number, dp: number) => v.toLocaleString('vi-VN', { maximumFractionDigits: dp });

export function fanSpecErrors(f: Partial<FanSpec>): string | null {
  if (!(Number(f.airflowM3h) > 0)) return 'Nhập lưu lượng (m³/h) lớn hơn 0';
  if (!(Number(f.pressurePa) > 0)) return 'Nhập cột áp (Pa) lớn hơn 0';
  if (!(Number(f.motorKw) > 0)) return 'Nhập công suất động cơ (kW) lớn hơn 0';
  if (f.rpm != null && !(Number(f.rpm) > 0)) return 'Tốc độ phải lớn hơn 0';
  return null;
}

/** "Q=30.000 m3/h, H=250 Pa, P=4 kW, 380V/3 pha, 1.450 v/p, 250°C/2h" */
export function fanSpecText(f: FanSpec): string {
  const parts = [`Q=${vn(f.airflowM3h, 0)} m3/h`, `H=${vn(f.pressurePa, 0)} Pa`, `P=${vn(f.motorKw, 2)} kW`];
  if (f.voltage?.trim()) parts.push(f.voltage.trim());
  if (f.rpm) parts.push(`${vn(f.rpm, 0)} v/p`);
  if (f.heatRating?.trim()) parts.push(f.heatRating.trim());
  return parts.join(', ').slice(0, SPEC_MAX);
}

const num = (s: string) => Number(s.replace(/\s/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.'));

/** Đọc lại thông số từ quy cách / tên mã ("Q=23.000m3/h, H=150 Pa, P=2.2KW") để mở hộp có sẵn số. */
export function parseFanSpec(text: string | null | undefined): Partial<FanSpec> {
  const t = (text ?? '').normalize('NFC');
  const out: Partial<FanSpec> = {};
  const q = /Q\s*=\s*([\d.,\s]+?)(?:\s*-\s*[\d.,\s]+)?\s*m3\/h/i.exec(t);
  if (q) out.airflowM3h = num(q[1]!);
  const h = /H\s*=\s*([\d.,]+)\s*Pa/i.exec(t);
  if (h) out.pressurePa = num(h[1]!);
  const p = /P\s*=\s*([\d.,]+)\s*kw/i.exec(t);
  if (p) out.motorKw = Number(p[1]!.replace(',', '.'));
  const v = /(220|380)\s*v(?:\s*\/\s*([13])\s*(?:p|pha))?/i.exec(t);
  if (v) out.voltage = v[1] === '220' ? '220V/1 pha' : '380V/3 pha';
  const r = /([\d.]+)\s*(?:v\/p|rpm)/i.exec(t);
  if (r) out.rpm = num(r[1]!);
  for (const k of ['airflowM3h', 'pressurePa', 'motorKw', 'rpm'] as const) if (out[k] !== undefined && !(Number(out[k]) > 0)) delete out[k];
  return out;
}
